"use client";

import { useState } from "react";
import type { FormEvent } from "react";
import { useRouter } from "next/router";
import type { ConfirmationResult } from "firebase/auth";
import { useFirebaseAuth } from "@/lib/firebase-auth-context";
import { describeAuthError } from "@/lib/auth-errors";
import { ensureCustomerProfile } from "@/lib/customer-profile";
import { normalisePhone } from "@/lib/phone";
import type { QuickSignInBlock } from "@/lib/storefront-types";

// Unique container so the invisible reCAPTCHA never clashes with the one on
// the dedicated /sign-in page if both ever mount.
const RECAPTCHA_CONTAINER_ID = "rl-quicksignin-recaptcha";

const DEFAULT_HEADLINE = "Sign in for faster checkout";
const DEFAULT_SUBTEXT = "One tap with your phone number — no passwords, no forms.";
const DEFAULT_BUTTON_LABEL = "Get my code";

/**
 * Inline phone-OTP sign-in prompt rendered as a home-page content block.
 * Anonymous-only: it never renders for a signed-in customer, and stays hidden
 * until auth has resolved so a logged-in visitor never sees a flash. Reuses the
 * exact same auth plumbing as the dedicated /sign-in page. On a successful OTP
 * the auth-state change unmounts the block in place — no redirect needed.
 */
export function QuickSignInSection({ block }: { block: QuickSignInBlock }) {
  const router = useRouter();
  const { startPhoneVerification, confirmOtp, user, loading, configured } =
    useFirebaseAuth();

  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(
    null,
  );
  const [submittedPhone, setSubmittedPhone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!configured || loading || user) {
    return null;
  }

  const headline = block.headline?.trim() || DEFAULT_HEADLINE;
  const subtextHtml = block.bodyHtml?.trim() || DEFAULT_SUBTEXT;
  const buttonLabel = block.buttonLabel?.trim() || DEFAULT_BUTTON_LABEL;

  async function handlePhoneSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const e164 = normalisePhone(phone);
    if (!/^\+\d{8,15}$/.test(e164)) {
      setError("Please enter a valid phone number, including the country code.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await startPhoneVerification(e164, RECAPTCHA_CONTAINER_ID);
      setConfirmation(result);
      setSubmittedPhone(e164);
      setStep("otp");
    } catch (caught) {
      setError(describeAuthError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOtpSubmit(event: FormEvent) {
    event.preventDefault();
    if (!confirmation) {
      setStep("phone");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const signedInUser = await confirmOtp(confirmation, otp);
      try {
        await ensureCustomerProfile(
          signedInUser.uid,
          submittedPhone ?? signedInUser.phoneNumber ?? null,
        );
      } catch (profileError) {
        // Don't block sign-in if the Firestore profile seed is rejected — the
        // user is signed in either way. Log so it never fails invisibly.
        console.error(
          "[quick-sign-in] ensureCustomerProfile failed:",
          profileError,
        );
      }
      // Send the freshly signed-in customer to their account, matching the
      // dedicated /sign-in page.
      await router.push("/account");
    } catch (caught) {
      setError(describeAuthError(caught));
      setSubmitting(false);
    }
  }

  function handleChangeNumber() {
    setStep("phone");
    setOtp("");
    setConfirmation(null);
    setError(null);
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="storefront-page-panel rounded-[2rem] px-6 py-7 sm:px-8">
        <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
          Your account
        </p>
        <h2 className="storefront-page-title mt-2 text-[1.6rem] font-bold tracking-tight sm:text-[1.9rem]">
          {step === "phone" ? headline : "Enter the code"}
        </h2>
        {step === "phone" ? (
          <div
            className="storefront-page-body mt-2 text-sm leading-6"
            dangerouslySetInnerHTML={{ __html: subtextHtml }}
          />
        ) : (
          <p className="storefront-page-body mt-2 text-sm leading-6">
            We&apos;ve sent a 6-digit code to {submittedPhone ?? "your phone"}.
          </p>
        )}

        {step === "phone" ? (
          <form
            className="mt-5 flex flex-col gap-3 sm:flex-row"
            onSubmit={handlePhoneSubmit}
          >
            <input
              type="tel"
              required
              autoComplete="tel"
              inputMode="tel"
              aria-label="Mobile number"
              placeholder="+44 7700 900123"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className="storefront-page-input w-full flex-1 rounded-full px-5 py-3 text-base"
            />
            <button
              type="submit"
              disabled={submitting}
              className="storefront-page-button inline-flex min-h-12 items-center justify-center rounded-full px-6 text-base font-semibold disabled:opacity-60"
            >
              {submitting ? "Sending..." : buttonLabel}
            </button>
          </form>
        ) : (
          <form className="mt-5 space-y-3" onSubmit={handleOtpSubmit}>
            <input
              type="text"
              required
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              aria-label="6-digit code"
              placeholder="123456"
              value={otp}
              onChange={(event) =>
                setOtp(event.target.value.replace(/[^0-9]/g, ""))
              }
              className="storefront-page-input w-full rounded-full px-5 py-3 text-center text-2xl font-semibold tracking-[0.4em]"
            />
            <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={submitting || otp.length < 6}
                className="storefront-page-button inline-flex min-h-12 items-center justify-center rounded-full px-6 text-base font-semibold disabled:opacity-60"
              >
                {submitting ? "Signing in..." : "Sign in"}
              </button>
              <button
                type="button"
                onClick={handleChangeNumber}
                className="storefront-page-link text-center text-sm sm:text-left"
              >
                Use a different number
              </button>
            </div>
          </form>
        )}

        {error ? (
          <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <div id={RECAPTCHA_CONTAINER_ID} />
      </div>
    </section>
  );
}
